import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { connectSocket, disconnectSocket } from '../services/socket';

const AuthContext = createContext(null);

// Inactivity timeout from env in minutes (default: 30 min)
const INACTIVITY_TIMEOUT_MIN = parseInt(process.env.REACT_APP_INACTIVITY_TIMEOUT || '30', 10);
const INACTIVITY_TIMEOUT_MS = (Number.isFinite(INACTIVITY_TIMEOUT_MIN) && INACTIVITY_TIMEOUT_MIN > 0
  ? INACTIVITY_TIMEOUT_MIN
  : 30) * 60 * 1000;
const ACTIVITY_CHECK_INTERVAL_MS = 15 * 1000;
const LAST_ACTIVITY_KEY = 'last_activity_ts';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const inactivityIntervalRef = useRef(null);
  const inactivityToastShownRef = useRef(false);

  const markActivity = useCallback(() => {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    localStorage.removeItem('currentClinicId');
    setToken(null);
    setUser(null);
    disconnectSocket();
    inactivityToastShownRef.current = false;
    if (inactivityIntervalRef.current) {
      clearInterval(inactivityIntervalRef.current);
      inactivityIntervalRef.current = null;
    }
  }, []);

  // Track explicit user activity and periodically enforce inactivity timeout.
  useEffect(() => {
    if (!token || !user) {
      if (inactivityIntervalRef.current) {
        clearInterval(inactivityIntervalRef.current);
        inactivityIntervalRef.current = null;
      }
      return;
    }

    // Initialize activity timestamp when user becomes authenticated.
    if (!localStorage.getItem(LAST_ACTIVITY_KEY)) {
      markActivity();
    }

    const activityEvents = ['mousedown', 'keydown', 'touchstart', 'click'];
    const handleActivity = () => {
      markActivity();
      inactivityToastShownRef.current = false;
    };

    const checkInactivity = () => {
      const last = parseInt(localStorage.getItem(LAST_ACTIVITY_KEY) || '0', 10);
      const lastActivity = Number.isFinite(last) && last > 0 ? last : Date.now();
      if (Date.now() - lastActivity >= INACTIVITY_TIMEOUT_MS) {
        if (!inactivityToastShownRef.current) {
          toast.error('Session expired due to inactivity. Please login again.');
          inactivityToastShownRef.current = true;
        }
        logout();
      }
    };

    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity, true);
    });
    window.addEventListener('focus', handleActivity, true);
    document.addEventListener('visibilitychange', () => {
      // If user comes back after long idle period, enforce timeout immediately.
      if (document.visibilityState === 'visible') checkInactivity();
    });

    inactivityIntervalRef.current = setInterval(checkInactivity, ACTIVITY_CHECK_INTERVAL_MS);
    checkInactivity();

    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity, true);
      });
      window.removeEventListener('focus', handleActivity, true);
      if (inactivityIntervalRef.current) {
        clearInterval(inactivityIntervalRef.current);
        inactivityIntervalRef.current = null;
      }
    };
  }, [token, user, logout, markActivity]);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await api.get('/auth/me');
          setUser(response.data.data);
        } catch (error) {
          console.error('Auth init error:', error);
          logout();
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [token, logout]);

  useEffect(() => {
    if (token && user) {
      connectSocket(token);
    }
    if (!token) {
      disconnectSocket();
    }
  }, [token, user]);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { token: newToken, user: userData } = response.data.data;
    
    localStorage.setItem('token', newToken);
    localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
    setToken(newToken);
    setUser(userData);
    
    return response.data;
  };

  const updateProfile = async (data) => {
    const response = await api.put('/auth/update-profile', data);
    setUser(prev => ({ ...prev, ...response.data.data }));
    return response.data;
  };

  const refreshUser = async () => {
    if (token) {
      try {
        const response = await api.get('/auth/me');
        setUser(response.data.data);
        markActivity();
        return response.data.data;
      } catch (error) {
        console.error('Refresh user error:', error);
        logout();
      }
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    const response = await api.put('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    });
    return response.data;
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!token && !!user,
    login,
    logout,
    updateProfile,
    changePassword,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

